import React, { useState, useEffect } from 'react';

import InputField from 'components/FormFields/InputField';
import { legacyConvertCurrency, toCents } from 'utils/general';

/**
 * A controlled input component for handling currency values.
 * It displays a formatted currency string (e.g., "$1,234.56") but allows users
 * to edit the raw numeric value. The component's state is managed in cents.
 *
 * @param {object} props The component props.
 * @param {number} [props.cents=0] The initial value of the input in cents.
 * @param {string} [props.currency='USD'] The currency to use for formatting (e.g., 'USD').
 * @param {function(number): void} props.onChange A callback function that receives the updated value in cents.
 * @param {object} props.rest Additional props are passed down to the underlying `InputField` component.
 */
const CurrencyInput = ({
  cents = 0,
  currency = 'USD',
  onChange,
  ...rest
}) => {
  const [formattedAmount, setFormattedAmount] = useState('');
  const [inputValue, setInputValue] = useState('');

  const handleFocus = () => {
    // When focusing, show the raw value for editing.
    setInputValue(cents > 0 ? (cents / 100).toString() : '');
    setFormattedAmount('');
  };

  const handleChange = (value) => {
    setInputValue(value);
  };

  const updateFormattedAmount = () => {
    try {
      const newCents = toCents(inputValue);
      onChange(newCents);
      if (newCents > 0) {
        setFormattedAmount(legacyConvertCurrency(newCents, currency));
      } else {
        setFormattedAmount('');
      }
    } catch {
      // If input is invalid, revert to the last valid state from props.
      if (cents > 0) {
        setFormattedAmount(legacyConvertCurrency(cents, currency));
      } else {
        setFormattedAmount('');
      }
    }
    setInputValue('');
  };

  useEffect(() => {
    if (cents > 0) {
      setFormattedAmount(legacyConvertCurrency(cents, currency));
    } else {
      setFormattedAmount('');
    }
  }, [cents, currency]);

  return (
    <InputField
      {...rest}
      onFocus={handleFocus}
      onChange={(e) => handleChange(e.target.value)}
      onBlur={updateFormattedAmount}
      value={formattedAmount || inputValue}
    />
  );
};

export default CurrencyInput;
